import { apiClient } from '@/services/api/client';

/** Access/refresh pair issued by the platform's JWT endpoint. */
export interface TokenPair {
  access: string;
  refresh: string;
}

/** The refresh endpoint returns only a new access token; the refresh token is reused until it expires. */
export interface RefreshResponse {
  access: string;
}

/**
 * JWT endpoints on the platform API. The token endpoint authenticates with the
 * existing session cookie, so the caller needs no credentials of its own.
 */
export const tokenApi = {
  async obtainToken(): Promise<TokenPair> {
    return apiClient.post<TokenPair>('/api/token/');
  },

  async refreshToken(refresh: string): Promise<RefreshResponse> {
    return apiClient.post<RefreshResponse>('/api/token/refresh/', { refresh });
  },
};

/** Refresh this long before the access token's `exp`, so a request in flight doesn't arrive with an expired token. */
const EXPIRY_MARGIN_MS = 30_000;

/** A JWT's `exp` claim in epoch ms, or null when it can't be read. */
export function getTokenExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp } = JSON.parse(json) as { exp?: unknown };
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True when the token is expired or about to expire. An unreadable token counts as expired. */
function isExpiring(token: string): boolean {
  const expiry = getTokenExpiry(token);
  return expiry === null || expiry - EXPIRY_MARGIN_MS <= Date.now();
}

// Held in memory only, never in browser storage — the session cookie can mint a new pair after a reload.
let tokens: TokenPair | null = null;
let pending: Promise<string> | null = null;

async function renewAccessToken(): Promise<string> {
  if (tokens && !isExpiring(tokens.refresh)) {
    try {
      const { access } = await tokenApi.refreshToken(tokens.refresh);
      tokens = { ...tokens, access };
      return tokens.access;
    } catch {
      // A rejected refresh token (revoked, blacklisted) falls through to a fresh pair.
    }
  }
  tokens = await tokenApi.obtainToken();
  return tokens.access;
}

/**
 * A valid access token, fetching a new pair or refreshing the current one when
 * it's expiring. Concurrent callers share a single in-flight request.
 */
export async function getAccessToken(): Promise<string> {
  if (tokens && !isExpiring(tokens.access)) return tokens.access;
  pending ??= renewAccessToken().finally(() => {
    pending = null;
  });
  return pending;
}

/** Drop the cached tokens, e.g. on logout or after the server rejects the access token. */
export function clearTokens(): void {
  tokens = null;
  pending = null;
}
